import { createContext, useState, useEffect, useCallback } from 'react'
import { INITIAL_ROWS_PER_PAGE } from '../../../pages/Products/components/TableDataPagination'
import { Paginator, defaultPaginator } from '../../../pages/Products/context/productos.vendedor.context'
import { getLinks, getPagos } from '../services/admin.local.services'

export interface LinksDTO {
  idContratoPagoLink: number
  idContrato: number
  platform: string
  idExterno: string
  link: string
  source: string
  fechaAlta: string
  fechaVencimiento: string
}

export interface PagosDTO {
  idContratoPago: number
  idContrato: number
  contratoPagoEstado: string
  contratoPagoEstadoLogica: string
  monto: number
  montoDevuelto: number
  saldoAplicado: any
  pagoNroOperacion: string
  fechaPago: string
  fechaAlta: string
  fechaBaja: string
  idContratoPagoLink: number
  mensajeResultado: string
  nuevoContrato: boolean
  historialCambios: any[]
}

export interface LinksLocal {
  page: number
  pageSize: number
  totalPages: number
  totalItems: number
  idContrato: number
  items: LinksDTO[]
}

export interface PagosLocal {
  page: number
  pageSize: number
  totalPages: number
  totalItems: number
  idContrato: number
  items: PagosDTO[]
}

export const defaultData: any = {
  page: 0,
  pageSize: INITIAL_ROWS_PER_PAGE,
  totalPages: 0,
  totalItems: 0,
  items: []
}

interface ProductosVendedorContextProps {
  fetchLinks: (idContrato: number) => void
  fetchPagos: (idContrato: number) => void
  linksContrato: LinksLocal
  setLinksContrato: (links: LinksLocal) => void
  pagosContrato: PagosLocal
  setPagosContrato: (pagos: PagosLocal) => void
  loading: boolean
  paginatorLinks: Paginator
  paginatorPagos: Paginator
  pageLinks: number
  setPageLinks: (p: number) => void
  pageSizeLinks: number
  setPageSizeLinks: (size: number) => void
  pagePagos: number
  setPagePagos: (p: number) => void
  pageSizePagos: number
  setPageSizePagos: (size: number) => void
}

// create context
export const DataContratosContext = createContext<ProductosVendedorContextProps>({
  fetchLinks: () => {},
  fetchPagos: () => {},
  linksContrato: defaultData,
  setLinksContrato: () => {},
  pagosContrato: defaultData,
  setPagosContrato: () => {},
  loading: false,
  paginatorLinks: defaultPaginator,
  paginatorPagos: defaultPaginator,
  pageLinks: 0,
  setPageLinks: () => {},
  pageSizeLinks: INITIAL_ROWS_PER_PAGE,
  setPageSizeLinks: () => {},
  pagePagos: 0,
  setPagePagos: () => {},
  pageSizePagos: INITIAL_ROWS_PER_PAGE,
  setPageSizePagos: () => {}
})

// create provider
export const DataContratosProvider = ({ children }: any) => {
  const [linksContrato, setLinksContrato] = useState<LinksLocal>(defaultData)
  const [pagosContrato, setPagosContrato] = useState<PagosLocal>(defaultData)
  const [paginatorLinks] = useState<Paginator>(defaultPaginator)
  const [paginatorPagos] = useState<Paginator>(defaultPaginator)
  const [pageLinks, setPageLinks] = useState<number>(0)
  const [pageSizeLinks, setPageSizeLinks] = useState<number>(INITIAL_ROWS_PER_PAGE)
  const [pagePagos, setPagePagos] = useState<number>(0)
  const [pageSizePagos, setPageSizePagos] = useState<number>(INITIAL_ROWS_PER_PAGE)
  const [loading, setLoading] = useState<boolean>(false)

  paginatorLinks.handleChangePage = (e: any, newPage: number) => {
    setPageLinks(newPage)
  }

  paginatorLinks.handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPageSizeLinks(parseInt(event.target.value, 10))
    setPageLinks(0)
  }

  paginatorPagos.handleChangePage = (e: any, newPage: number) => {
    setPagePagos(newPage)
  }

  paginatorPagos.handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPageSizePagos(parseInt(event.target.value, 10))
    setPagePagos(0)
  }

  const fetchLinks = useCallback(async (idContrato: number) => {
    setLoading(true)
    try {
      const response = await getLinks(idContrato, pageLinks, pageSizeLinks)
      const data = response.data.data
      const newLinks: LinksLocal = {
        page: data.page,
        pageSize: data.pageSize,
        totalPages: data.totalPages,
        totalItems: data.totalElements,
        idContrato: data.results[0]?.idContrato || 0,
        items: data.results
      }
      setLinksContrato(newLinks)
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchPagos = useCallback(async (idContrato: number) => {
    setLoading(true)
    try {
      const response = await getPagos(idContrato, pagePagos, pageSizePagos)
      const data = response.data.data
      const newPagos: PagosLocal = {
        page: data.page,
        pageSize: data.pageSize,
        totalPages: data.totalPages,
        totalItems: data.totalElements,
        idContrato: data.results[0]?.idContrato || 0,
        items: data.results
      }
      setPagosContrato(newPagos)
    } catch (error) {
      console.error('Error fetching data: ', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    linksContrato.idContrato && fetchLinks(linksContrato.idContrato)
  }, [pageLinks, pageSizeLinks])

  useEffect(() => {
    pagosContrato.idContrato && fetchPagos(pagosContrato.idContrato)
  }, [pagePagos, pageSizePagos])

  return (
    <DataContratosContext.Provider
      value={{
        fetchLinks,
        fetchPagos,
        linksContrato,
        setLinksContrato,
        pagosContrato,
        setPagosContrato,
        loading,
        paginatorLinks,
        paginatorPagos,
        pageLinks,
        setPageLinks,
        pageSizeLinks,
        setPageSizeLinks,
        pagePagos,
        setPagePagos,
        pageSizePagos,
        setPageSizePagos
      }}
    >
      {children}
    </DataContratosContext.Provider>
  )
}
